/**
 * "Add to home screen" support (docs/mobile-pwa.md).
 *  - Android / desktop Chrome: the browser's `beforeinstallprompt` event, captured as early as possible.
 *  - iOS Safari has no install API: we show the Share -> "Add to Home Screen" steps instead.
 * The player's "not now" is remembered in localStorage (every access wrapped: private mode and blocked storage must not break the page).
 */
export const INSTALL_DISMISS_KEY = 'pg-install-dismissed';
/** After a dismissal the hint stays away for this long. */
export const INSTALL_DISMISS_MS = 30 * 24 * 3600 * 1000;

export type InstallMode = 'installed' | 'dismissed' | 'prompt' | 'ios' | 'none';

export interface InstallEnv {
  userAgent: string;
  maxTouchPoints: number;
  standalone: boolean;
  hasPrompt: boolean;
  dismissedAt: number | null;
  now: number;
}

/** iPhone / iPad (iPadOS 13+ reports a Mac user agent but has touch points) in Safari itself: not Chrome/Firefox/Edge/in-app browsers. */
export function isIosSafari(userAgent: string, maxTouchPoints: number): boolean {
  const ios = /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  if (!ios) return false;
  if (/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|GSA|FBAN|FBAV|Instagram|Line\//.test(userAgent)) return false;
  return /Safari/.test(userAgent);
}

/** Pure decision: which hint (if any) to show. */
export function installMode(env: InstallEnv): InstallMode {
  if (env.standalone) return 'installed';
  if (env.dismissedAt !== null && env.now - env.dismissedAt < INSTALL_DISMISS_MS) return 'dismissed';
  if (env.hasPrompt) return 'prompt';
  if (isIosSafari(env.userAgent, env.maxTouchPoints)) return 'ios';
  return 'none';
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function readDismissedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(INSTALL_DISMISS_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}

export function rememberDismissal(): void {
  try { window.localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now())); } catch { /* storage unavailable: the hint may come back, nothing breaks */ }
  notify();
}

export function currentInstallMode(): InstallMode {
  return installMode({
    userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints ?? 0, standalone: isStandalone(),
    hasPrompt: deferred !== null, dismissedAt: readDismissedAt(), now: Date.now(),
  });
}

export function subscribeInstall(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Opens the browser's own install dialog (Android / Chrome). Resolves true when the player accepted. */
export async function promptInstall(): Promise<boolean> {
  const ev = deferred;
  if (!ev) return false;
  deferred = null;
  notify();
  try {
    await ev.prompt();
    return (await ev.userChoice).outcome === 'accepted';
  } catch { return false; }
}

/** Called once from main.tsx so an early `beforeinstallprompt` is not missed. */
export function initInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e as BeforeInstallPromptEvent; notify(); });
  window.addEventListener('appinstalled', () => { deferred = null; notify(); });
}
